// `@stripe/stripe-js/pure` skips the side-effect script injection that the
// default entry runs at import time, so importing this module costs nothing
// until a caller invokes getStripe() — typically when the payment form mounts.
import * as Sentry from '@sentry/react';
import type { Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import config from '@/config';

// A blocked script fires onerror promptly, but a stalled request (captive
// portal, proxy blackhole) fires neither load nor error, and Stripe.js ships no
// timeout of its own. Unbounded, that leaves the payment form loading forever
// and emits no Sentry signal at all.
export const LOAD_TIMEOUT_MS = 20_000;

// Which failure happened matters to the UI, because only one of them is
// recoverable in place:
//
// 'blocked' — the injected <script> fired `error`, which is what an ad blocker,
//   privacy extension or network filter produces. Stripe.js clears its own
//   internal cache on that rejection and re-injects the tag on the next call
//   (`dist/pure.mjs` loadScript), so calling getStripe() again is a real
//   network attempt and "try again" can succeed.
//
// 'timeout' — the request stalled and never settled. Stripe.js only resets its
//   cache from a `.catch` on that inner promise, so the promise stays pending
//   and a later call short-circuits on `if (stripePromise !== null)` and returns
//   the SAME pending promise without re-injecting anything. Retrying in place
//   cannot work; only a page reload clears it.
export type StripeLoadFailureKind = 'blocked' | 'timeout';

// Which flow mounted the form, for breaking down Sentry volume by surface.
export type StripeLoadSource = 'smb_self_service' | 'partner_signup';

export type StripeLoadResult =
  | { status: 'ready'; stripe: Stripe }
  | { status: 'failed'; kind: StripeLoadFailureKind };

let resultPromise: Promise<StripeLoadResult> | null = null;

// Latched per kind AND source rather than globally: a retry loop shouldn't
// amplify events, but a 'timeout' after a 'blocked' is new information, and so
// is the same kind failing on a second surface. Latching on kind alone meant the
// first flow to fail was the only one that ever reported, which made the
// stripe_load_source breakdown useless.
const reportedFailures = new Set<string>();

class StripeLoadTimeoutError extends Error {
  constructor() {
    super('Timed out loading Stripe.js');
    this.name = 'StripeLoadTimeoutError';
  }
}

function handleFailure(
  err: unknown,
  kind: StripeLoadFailureKind,
  source?: StripeLoadSource
): StripeLoadResult {
  // Only 'blocked' is worth re-attempting: Stripe.js clears its own cache on
  // that rejection, so dropping ours makes the next call a real network retry.
  // A stalled load leaves Stripe.js holding a pending promise forever, so
  // clearing our memo would only make the next mount race a fresh 20s timer
  // against that same hung promise — another full timeout before the UI can
  // offer the reload that is the actual fix. Keeping the settled result
  // memoized sends later mounts straight to it.
  if (kind === 'blocked') {
    resultPromise = null;
  }
  const latchKey = `${kind}:${source ?? 'unknown'}`;
  if (!reportedFailures.has(latchKey)) {
    reportedFailures.add(latchKey);
    // level: 'warning' — this is a known, already-handled client-side
    // condition, not an app bug. The default 'error' severity overstated the
    // urgency of a single event.
    Sentry.captureException(err, {
      level: 'warning',
      tags: {
        stripe_load_failed: 'true',
        stripe_load_failure_kind: kind,
        ...(source ? { stripe_load_source: source } : {}),
      },
    });
  }
  return { status: 'failed', kind };
}

function loadWithTimeout(): Promise<Stripe | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new StripeLoadTimeoutError()), LOAD_TIMEOUT_MS);
  });
  return Promise.race([loadStripe(config.stripePublishableKey), timeout]).finally(() => {
    clearTimeout(timer);
  });
}

// Never rejects: failures resolve to a `failed` result so callers can render a
// fallback instead of letting the rejection escape as an unhandledrejection.
// Failures only reach Sentry when something actually called getStripe (i.e. a
// payment form mounted), so we get clean signal on real impact without the
// public-page noise.
export function getStripe(source?: StripeLoadSource): Promise<StripeLoadResult> {
  if (!resultPromise) {
    resultPromise = loadWithTimeout()
      .then((stripe): StripeLoadResult => {
        if (!stripe) {
          // Only reachable without a DOM (Stripe.js resolves null when imported
          // server-side); treat it as unavailable rather than crashing.
          return handleFailure(new Error('Stripe.js unavailable'), 'blocked', source);
        }
        return { status: 'ready', stripe };
      })
      .catch((err: unknown) =>
        handleFailure(err, err instanceof StripeLoadTimeoutError ? 'timeout' : 'blocked', source)
      );
  }
  return resultPromise;
}
